import { isEmptyString, value } from '@takeshape/util';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
import { getInitialData } from "../util/initial-data.js";
import { fixObfuscatedProp } from "../util/props.js";
const serviceProvider = {
    name: 'reviewsIo',
    title: 'REVIEWS.io',
    description: 'Collect genuine text, video and photo reviews from your happy customers.',
    categories: ['ecommerce', 'reviews'],
    links: [
        { title: 'Homepage', url: 'https://www.reviews.io/' },
        { title: 'Docs', url: '/docs/services/providers/reviews-io' }
    ],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('reviews-io'),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'namespace', 'storeId', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: 'REVIEWS.io'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: 'reviews-io'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'ReviewsIo'
            },
            storeId: {
                title: 'Store ID',
                type: 'string'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    apiKey: {
                        title: 'API Key',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['apiKey']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'storeId', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable name for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'A unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            storeId: {
                widget: 'singleLineText',
                instructions: 'Store ID from Integrations > API'
            },
            authentication: {
                properties: {
                    apiKey: {
                        widget: 'sensitiveField',
                        instructions: 'API Key from Integrations > API'
                    }
                },
                order: ['apiKey']
            }
        }
    }),
    getConfig(formData) {
        const config = {
            id: formData.id,
            title: formData.title,
            provider: 'reviewsIo',
            serviceType: 'openapi',
            authenticationType: 'searchParams',
            namespace: formData.namespace,
            options: {
                endpoint: 'https://api.reviews.io/',
                spec: 'https://raw.githubusercontent.com/takeshape/openapi-specs/6b0b5e784ad8055a3666a8a8f70c655b4161f2e6/specs/reviews.io/v0.0.0.yml',
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION,
                useRestfulNames: true,
                searchParams: {
                    store: formData.storeId
                }
            }
        };
        if (!formData.authentication) {
            return config;
        }
        if (isEmptyString(formData.authentication.apiKey)) {
            config.authentication = null;
            return config;
        }
        const apiKey = fixObfuscatedProp(formData.authentication.apiKey);
        if (apiKey) {
            // Valid auth update, rebuild the auth array. Do this to get the store
            // param handled at the service level since we don't set resolver searchParams
            // from the config.options object.
            config.authentication = {
                type: 'searchParams',
                params: [
                    {
                        name: 'apikey',
                        value: apiKey
                    }
                ]
            };
        }
        return config;
    },
    getInitialData(serviceConfig) {
        const initialData = getInitialData(serviceProvider, serviceConfig);
        // Relocate this value for editing
        initialData.storeId = serviceConfig?.options?.searchParams.store;
        return initialData;
    },
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint', 'options.spec', 'options.searchParams.store']),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: endpointWithPath('/merchant/invitations')
    })
};
export default serviceProvider;
